import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useAppContext } from 'providers/AppProvider';
import { tooltipFormatterCandle } from 'helpers/echart-utils';
import { format, parse } from 'date-fns';

interface ScoreData {
  calldate: string;
  avgscore: number;
  avgscorea: number;
  avgscorec: number;
  minscore: number;
  maxscore: number;
  numcalls: number;
}

interface ScoreEvolutionChartProps {
  data: ScoreData[];
}

const ScoreEvolutionChart: React.FC<ScoreEvolutionChartProps> = ({ data }) => {
  // eslint-disable-next-line
  const { config: { isDark }, getThemeColor, addAlphaToHexColor } = useAppContext();
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chartRef.current && data.length > 0) {
      const chart = echarts.init(chartRef.current);
      // eslint-disable-next-line
      const upColor = !isDark ? 'rgba(192, 0, 0, 0.4)' : 'rgba(220, 80, 80, 0.7)';
      const downColor = 'rgba(192, 0, 0, 0.5)';
      // eslint-disable-next-line
      const ma10Color = !isDark ? getThemeColor('primary') : getThemeColor('primary');
      const volumeColor = 'rgba(75, 75, 90, 0.5)';

      const splitData = (rawData: ScoreData[]) => {
        const categoryData: string[] = [];
        const values: number[][] = [];
        const volumes: [number, number, number][] = [];
        const averages: [number, number][] = [];

        rawData.forEach((item, index) => {
          categoryData.push(item.calldate);
          values.push([
            item.avgscorea,
            item.avgscorec,
            item.minscore,
            item.maxscore,
            item.avgscore
          ]);
          // eslint-disable-next-line
          volumes.push([index, item.numcalls, item.avgscorea > item.avgscorec ? 1 : -1]);
          // eslint-disable-next-line
          averages.push([index, item.avgscore]);
        });

        return { categoryData, values, volumes, averages };
      };

      const processedData = splitData(data);

      const option: echarts.EChartsOption = {
        animation: true,
        tooltip: {
          trigger: 'axis',
          // eslint-disable-next-line
          formatter: (params: any) => tooltipFormatterCandle(params, 'MMM DD, YYYY', 'color'),
          // eslint-disable-next-line
          backgroundColor: !isDark ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
          textStyle: {
            color: !isDark ? '#202020' : '#ccc',
            fontFamily: 'Nunito Sans',
            fontSize: 14
          },
          borderWidth: 1,
          // eslint-disable-next-line
          borderColor: !isDark ? 'rgba(49, 55, 74, 0.4)' : 'rgba(140, 140, 180, 0.8)',
          padding: 10
        },
        axisPointer: {
          link: [
            {
              xAxisIndex: 'all'
            }
          ],
          label: {
            backgroundColor: '#777'
          }
        },
        toolbox: {
          itemSize: 10,
          right: '30',
          showTitle: false,
          show: true,
          iconStyle: {
            // eslint-disable-next-line
            borderColor: !isDark ? 'rgba(160, 160, 190, 0.9)' : 'rgba(100, 100, 150, 0.9)'
          },
          emphasis: {
            iconStyle: { borderColor: getThemeColor('primary') }
          },
          feature: {
            mark: { show: false },
            magicType: { show: false, type: ['line', 'bar'] },
            dataZoom: { show: false },
            dataView: { show: false, readOnly: true },
            restore: { show: false },
            saveAsImage: { show: true },
            brush: { type: ['lineX', 'clear'], show: false }
          }
        },
        brush: {
          xAxisIndex: 'all',
          brushLink: 'all',
          outOfBrush: {
            colorAlpha: 0.1
          }
        },
        visualMap: {
          show: false,
          seriesIndex: 5,
          dimension: 2,
          pieces: [
            {
              value: 1,
              color: downColor
            },
            {
              value: -1,
              color: upColor
            }
          ]
        },
        grid: [
          {
            left: '30',
            right: '40',
            top: '20',
            height: '210'
          },
          {
            left: '30',
            right: '40',
            top: '250',
            height: '43'
          }
        ],
        xAxis: [
          {
            type: 'category',
            data: processedData.categoryData,
            boundaryGap: false,
            axisLine: {
              onZero: false,
              lineStyle: {
                // eslint-disable-next-line
                color: !isDark ? 'rgba(160, 160, 190, 0.5)' : 'rgba(100, 100, 150, 0.5)'
              }
            },
            splitLine: { show: false },
            min: 'dataMin',
            max: 'dataMax',
            axisPointer: {
              z: 100
            },
            axisLabel: {
              fontSize: 9,
              // eslint-disable-next-line
              color: !isDark ? 'rgba(110, 110, 130, 0.9)' : 'rgba(130, 130, 140, 0.9)',
              formatter: (value: string) => {
                const date = parse(value, 'yyyy-MM-dd', new Date());
                return format(date, 'MMM dd, yyyy');
              }
            }
          },
          {
            type: 'category',
            gridIndex: 1,
            data: processedData.categoryData,
            boundaryGap: false,
            axisLine: { show: false, onZero: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            min: 'dataMin',
            max: 'dataMax'
          }
        ],
        yAxis: [
          {
            type: 'value',
            //min: 1,
            //max: 10,
            splitNumber: 5,
            scale: true,
            splitArea: {
              show: false // Disable grid bands
            },
            axisLabel: {
              fontSize: 10,
              formatter: (value: number) => value.toFixed(2)
            },
            splitLine: {
              lineStyle: {
                // eslint-disable-next-line
                color: !isDark ? 'rgba(160, 160, 190, 0.2)' : 'rgba(100, 100, 150, 0.2)' // Customize grid line color
              }
            }
          },
          {
            scale: true,
            gridIndex: 1,
            splitNumber: 2,
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false }
          }
        ],
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: [0, 1],
            start: 0,
            end: 100,
            filterMode: 'filter',
            zoomOnMouseWheel: false,
            moveOnMouseWheel: false,
            moveOnMouseMove: true
          },
          {
            show: true,
            xAxisIndex: [0, 1],
            type: 'slider',
            left: '25',
            right: '40',
            top: '300',
            height: '20',
            start: 0,
            end: 100,
            fillerColor: addAlphaToHexColor(getThemeColor('primary'), 0.1),
            borderColor: addAlphaToHexColor(getThemeColor('primary'), 0.1),
            handleStyle: {
              color: getThemeColor('primary-light'),
              borderColor: getThemeColor('primary-light')
            },
            textStyle: {
              color: 'rgba(0, 0, 0, 0)',
              fontSize: 10
            },
            brushSelect: false
          }
        ],
        series: [
          {
            name: 'Average Score',
            type: 'line',
            data: processedData.averages,
            smooth: true,
            lineStyle: {
              opacity: 0.5,
              color: ma10Color // Aplicamos el color naranja a MA10
            },
            itemStyle: {
              color: ma10Color // Color del marcador
            },
            symbol: 'circle', // Forma del marcador
            symbolSize: 6, // Tamaño del marcador
            showSymbol: true // Asegura que los marcadores sean visibles
          },
          {
            name: 'Score range',
            type: 'candlestick',
            //data: processedData.values,
            itemStyle: {
              color: upColor,
              color0: downColor,
              borderColor: undefined,
              borderColor0: undefined
            }
          },
          {
            name: 'Volume (calls)',
            type: 'bar',
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: processedData.volumes,
            itemStyle: {
              color: volumeColor // Aplicamos el color gris al volumen
            }
          }
        ]
      };

      chart.setOption(option);

      const handleResize = () => {
        const containerWidth = chartRef.current?.clientWidth || 0;
        chart.resize({ width: containerWidth });
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        chart.dispose();
      };
    }
  }, [data, isDark]);

  return <div ref={chartRef} style={{ width: '100%', height: '330px' }} />;
};

export default ScoreEvolutionChart;
